import React, { useState, useCallback, useEffect, useRef } from 'react';
import { DiceValue } from '../../types';
import Die from '../Die';
import DiceBackground from '../DiceBackground';
import { calculateColumnScore } from '../../utils';
import { markTutorialCompleted } from '../../hooks/useTutorial';
import { useTranslation } from '../../i18n';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../../utils/analytics';
import { useSound } from '../../hooks/useSound';

// ── Types ────────────────────────────────────────────────────────────────────

type Grid = (DiceValue | null)[][];

type StepType =
    | 'intro'       // Just show a message, user clicks NEXT
    | 'place'       // Player must click the highlighted column
    | 'opponent'    // Opponent auto-places after a delay
    | 'slaughter'   // Show destruction result
    | 'score'       // Show scoring explanation
    | 'end';        // Tutorial complete

interface ScriptStep {
    type: StepType;
    messageKey: string;
    /** Die value shown in center (player's die) */
    dieValue?: DiceValue;
    /** Which column (0-2) the player must place in */
    targetCol?: number;
    /** Die value the opponent places */
    opponentDie?: DiceValue;
    /** Which column (0-2) opponent places in */
    opponentCol?: number;
    /** Highlight area: 'player' | 'opponent' | null */
    highlight?: 'player' | 'opponent' | null;
    /** Tooltip position */
    position: 'top' | 'center' | 'bottom';
}

// ── Script ───────────────────────────────────────────────────────────────────

const SCRIPT: ScriptStep[] = [
    // Step 0: Welcome — highlight player grid
    {
        type: 'intro',
        messageKey: 'tutS1',
        highlight: 'player',
        position: 'bottom',
    },
    // Step 1: Opponent grid intro
    {
        type: 'intro',
        messageKey: 'tutS2',
        highlight: 'opponent',
        position: 'top',
    },
    // Step 2: Roll a 3, player places in col 1
    {
        type: 'place',
        messageKey: 'tutS3',
        dieValue: 3,
        targetCol: 1,
        position: 'center',
    },
    // Step 3: Opponent places a 5 in col 0
    {
        type: 'opponent',
        messageKey: 'tutS4',
        opponentDie: 5,
        opponentCol: 0,
        position: 'top',
    },
    // Step 4: Another 3, same column — scoring multiplier
    {
        type: 'place',
        messageKey: 'tutS5',
        dieValue: 3,
        targetCol: 1,
        position: 'center',
    },
    // Step 5: Opponent places 3 in col 1
    {
        type: 'opponent',
        messageKey: 'tutS6',
        opponentDie: 3,
        opponentCol: 1,
        position: 'top',
    },
    // Step 6: Third 3 — destroy mechanic!
    {
        type: 'place',
        messageKey: 'tutS7',
        dieValue: 3,
        targetCol: 1,
        position: 'center',
    },
    // Step 7: Slaughter result
    {
        type: 'slaughter',
        messageKey: 'tutS8',
        position: 'top',
    },
    // Step 8: Score explanation
    {
        type: 'score',
        messageKey: 'tutS9',
        highlight: 'player',
        position: 'bottom',
    },
    // Step 9: Done!
    {
        type: 'end',
        messageKey: 'tutS10',
        position: 'center',
    },
];

// ── Empty grid helper ────────────────────────────────────────────────────────

const emptyGrid = (): Grid => [
    [null, null, null],
    [null, null, null],
    [null, null, null],
];

const placeInGrid = (grid: Grid, col: number, value: DiceValue): Grid => {
    const newGrid = grid.map((c) => [...c]);
    const column = newGrid[col];
    const emptyIdx = column.indexOf(null);
    if (emptyIdx !== -1) {
        column[emptyIdx] = value;
    }
    return newGrid;
};

const removeFromGrid = (grid: Grid, col: number, value: DiceValue): Grid => {
    const newGrid = grid.map((c) => [...c]);
    const column = newGrid[col];
    // Remove all matching values
    for (let i = 0; i < column.length; i++) {
        if (column[i] === value) {
            column[i] = null;
        }
    }
    // Compact: move nulls to end
    const nonNull = column.filter((v) => v !== null);
    while (nonNull.length < 3) nonNull.push(null);
    newGrid[col] = nonNull as (DiceValue | null)[];
    return newGrid;
};

// ── Component ────────────────────────────────────────────────────────────────

interface TutorialGameProps {
    isMuted?: boolean;
}

const TutorialGame: React.FC<TutorialGameProps> = ({ isMuted = false }) => {
    const t = useTranslation();
    const navigate = useNavigate();
    const { triggerSound } = useSound(isMuted);

    const [stepIdx, setStepIdx] = useState(0);
    const [playerGrid, setPlayerGrid] = useState<Grid>(emptyGrid);
    const [opponentGrid, setOpponentGrid] = useState<Grid>(emptyGrid);
    const [lastAction, setLastAction] = useState<{ col: number; target: 'player' | 'opponent' } | null>(null);
    const [isOpponentPlacing, setIsOpponentPlacing] = useState(false);
    const [showDieRoll, setShowDieRoll] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [viewport, setViewport] = useState(() => ({
        width: window.innerWidth,
        height: window.innerHeight,
    }));

    const step = SCRIPT[stepIdx];
    const isLastStep = stepIdx === SCRIPT.length - 1;

    // Cleanup timeouts
    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setViewport({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const boardScale = (() => {
        if (viewport.width >= 1024) return 1;
        const { width, height } = viewport;
        const aspect = height / Math.max(width, 1);

        let scale = 1;
        if (height <= 650) scale = 0.76;
        else if (height <= 710) scale = 0.84;
        else if (height <= 780) scale = 0.9;
        else if (height <= 860) scale = 0.95;

        if (aspect < 1.65) scale = Math.min(scale, 0.88);
        if (width <= 360) scale = Math.min(scale, 0.86);

        return Math.max(0.74, Math.min(scale, 1));
    })();

    const boardStyle = boardScale < 1 ? { transform: `scale(${boardScale})`, transformOrigin: 'center' as const } : undefined;

    // Auto-trigger opponent placement
    useEffect(() => {
        if (step?.type === 'opponent' && !isOpponentPlacing) {
            setIsOpponentPlacing(true);
            triggerSound('roll');
            // Short delay to show the message, then auto-place
            timeoutRef.current = setTimeout(() => {
                if (step.opponentDie && step.opponentCol !== undefined) {
                    setOpponentGrid((prev) => placeInGrid(prev, step.opponentCol!, step.opponentDie!));
                    setLastAction({ col: step.opponentCol!, target: 'opponent' });
                    triggerSound('place');
                }
                // Auto-advance after a brief pause
                timeoutRef.current = setTimeout(() => {
                    setIsOpponentPlacing(false);
                    setLastAction(null);
                    setStepIdx((prev) => prev + 1);
                }, 800);
            }, 1200);
        }
    }, [stepIdx, step?.type, isOpponentPlacing, step?.opponentCol, step?.opponentDie, triggerSound]);

    // Handle slaughter step: remove opponent's matching dice
    useEffect(() => {
        if (step?.type === 'slaughter') {
            // The die was just placed in col 1 (a 3). Destroy opponent's 3 in same col.
            timeoutRef.current = setTimeout(() => {
                setOpponentGrid((prev) => removeFromGrid(prev, 1, 3));
                setLastAction({ col: 1, target: 'opponent' });
                triggerSound('destroy');
                timeoutRef.current = setTimeout(() => {
                    setLastAction(null);
                }, 600);
            }, 600);
        }
    }, [stepIdx, step?.type, triggerSound]);

    // Die roll animation for place steps
    useEffect(() => {
        if (step?.type === 'place') {
            triggerSound('roll');
            setShowDieRoll(true);
            timeoutRef.current = setTimeout(() => {
                setShowDieRoll(false);
            }, 500);
        }
    }, [stepIdx, step?.type, triggerSound]);

    // Player clicks a column
    const handleColumnClick = useCallback(
        (colIdx: number) => {
            if (step?.type !== 'place' || colIdx !== step.targetCol) return;
            if (step.dieValue === undefined) return;

            setPlayerGrid((prev) => placeInGrid(prev, colIdx, step.dieValue!));
            setLastAction({ col: colIdx, target: 'player' });
            triggerSound('place');

            timeoutRef.current = setTimeout(() => {
                setLastAction(null);
                setStepIdx((prev) => prev + 1);
            }, 500);
        },
        [step, triggerSound]
    );

    // Next button (for intro/slaughter/score/end steps)
    const handleNext = useCallback(() => {
        if (isLastStep) {
            triggerSound('win');
            markTutorialCompleted();
            trackEvent('tutorial_completed');
            navigate('/');
            return;
        }
        setStepIdx((prev) => prev + 1);
    }, [isLastStep, navigate, triggerSound]);

    // Skip tutorial
    const handleSkip = useCallback(() => {
        markTutorialCompleted();
        trackEvent('tutorial_skipped');
        navigate('/');
    }, [navigate]);

    // Calculate totals
    const playerTotal = playerGrid.reduce(
        (sum, col) => sum + calculateColumnScore(col),
        0
    );
    const opponentTotal = opponentGrid.reduce(
        (sum, col) => sum + calculateColumnScore(col),
        0
    );

    // Determine which column to highlight for place steps
    const isPlaceStep = step?.type === 'place' && !showDieRoll;

    // ── Tooltip text ───────────────────────────────────────────────────────
    const messageText = t[step?.messageKey as keyof typeof t] as string;

    // Position helpers for tooltip
    const getTooltipPosition = () => {
        switch (step?.position) {
            case 'top':
                return 'top-[15%] left-1/2 -translate-x-1/2';
            case 'bottom':
                return 'bottom-[18%] left-1/2 -translate-x-1/2';
            case 'center':
            default:
                return 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';
        }
    };

    const showNextBtn = step?.type === 'intro' || step?.type === 'slaughter' || step?.type === 'score' || step?.type === 'end';

    return (
        <div className="h-[100dvh] flex flex-col items-center justify-between text-white p-2 lg:p-4 relative select-none bg-[#1a101f] overflow-hidden">
            <DiceBackground count={8} />

            {/* Header */}
            <header className="w-full relative shrink-0 pt-2 lg:pt-4 text-center">
                <button
                    onClick={handleSkip}
                    className="absolute top-2 lg:top-4 left-2 lg:left-4 text-[8px] lg:text-xs text-gray-500 hover:text-red-400 transition-colors z-10"
                >
                    ← {t.leave}
                </button>

                <div className="flex flex-col items-center justify-center w-full">
                    <div className="text-[8px] lg:text-sm text-theme mb-0.5 font-bold uppercase tracking-widest">
                        {t.tutTitle}
                    </div>
                    <h1 className="text-lg lg:text-4xl font-bold tracking-tighter text-theme drop-shadow-lg uppercase">
                        {t.dicepit}
                    </h1>
                </div>

                <div className="h-6 lg:h-10 flex items-center justify-center">
                    {step?.type === 'place' && !showDieRoll && (
                        <p className="text-[9px] lg:text-lg text-yellow-500 animate-pulse tracking-widest uppercase text-center">
                            {t.tutPlace}
                        </p>
                    )}
                </div>
            </header>

            <main className="flex-1 w-full flex flex-col lg:flex-row items-center justify-around lg:justify-center gap-4 py-2 lg:gap-16 overflow-hidden">
                {/* Player Grid */}
                <div className={`flex flex-col items-center shrink-0 order-3 lg:order-1 transition-all duration-300 ${step?.highlight === 'player' ? 'ring-2 ring-theme/60 rounded-lg p-1' : ''
                    }`}
                    style={boardStyle}
                >
                    <div className="flex gap-2 lg:gap-8">
                        {playerGrid.map((col, i) => (
                            <div
                                key={i}
                                onClick={() => handleColumnClick(i)}
                                className={`
                  flex flex-col gap-1.5 lg:gap-4 p-1.5 lg:p-6 pixel-border transition-all cursor-pointer group relative
                  ${isPlaceStep && i === step?.targetCol
                                        ? 'bg-theme/40 border-theme shadow-theme scale-105 animate-pulse ring-2 ring-theme'
                                        : isPlaceStep && i !== step?.targetCol
                                            ? 'bg-[#1a101f] opacity-30 pointer-events-none'
                                            : 'bg-[#1a101f]'}
                  ${lastAction?.target === 'player' && lastAction?.col === i ? 'shake' : ''}
                `}
                            >
                                {col.map((die, di) => (
                                    <Die key={di} value={die} size="md" className={die ? 'opacity-100' : 'opacity-20'} />
                                ))}
                                <div className="text-[8px] lg:text-lg text-center mt-1 lg:mt-3 font-bold text-yellow-500 font-mono">
                                    {calculateColumnScore(col)}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-1 lg:mt-4 text-[8px] lg:text-xl font-bold text-blue-400 tracking-widest uppercase flex items-center gap-2">
                        {t.you} • {playerTotal}
                    </div>
                </div>

                {/* Center: Die */}
                <div className="flex flex-row lg:flex-col items-center justify-center gap-4 lg:gap-10 shrink-0 order-2 lg:order-2">
                    <div className="pixel-card p-2 lg:p-8 flex flex-col items-center justify-center min-h-[70px] lg:min-h-[220px] min-w-[70px] lg:min-w-[220px] shadow-2xl relative">
                        {showDieRoll ? (
                            <Die value={null} isRolling={true} size="lg" />
                        ) : step?.dieValue ? (
                            <Die value={step.dieValue} size="lg" />
                        ) : isOpponentPlacing ? (
                            <Die value={null} isRolling={true} size="lg" />
                        ) : (
                            <div className="w-16 h-16 lg:w-32 lg:h-32 border-2 lg:border-4 border-dashed border-gray-700 rounded-lg flex items-center justify-center text-[8px] lg:text-xs text-gray-500 text-center p-1 uppercase overflow-hidden">
                                <span className="leading-tight">{t.wait}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Opponent Grid */}
                <div className={`flex flex-col items-center shrink-0 order-1 lg:order-3 transition-all duration-300 ${step?.highlight === 'opponent' ? 'ring-2 ring-red-500/60 rounded-lg p-1' : ''
                    }`}
                    style={boardStyle}
                >
                    <div className="mb-1 lg:mb-4 text-[8px] lg:text-xl font-bold text-red-400 tracking-widest uppercase flex items-center gap-2">
                        {t.pitLord} • {opponentTotal}
                    </div>
                    <div className="flex gap-2 lg:gap-8">
                        {opponentGrid.map((col, i) => (
                            <div
                                key={i}
                                className={`flex flex-col gap-1.5 lg:gap-4 p-1.5 lg:p-6 pixel-border bg-[#1a101f] transition-all ${lastAction?.target === 'opponent' && lastAction?.col === i
                                    ? 'shake bg-red-900/40'
                                    : ''
                                    }`}
                            >
                                {col.map((die, di) => (
                                    <Die key={di} value={die} size="md" className={die ? 'opacity-100' : 'opacity-20'} />
                                ))}
                                <div className="text-[8px] lg:text-lg text-center mt-1 lg:mt-3 font-bold text-yellow-500 font-mono">
                                    {calculateColumnScore(col)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>

            <footer className="w-full text-center pb-2 lg:pb-6 shrink-0">
                <p className="text-[8px] lg:text-xs text-gray-400 font-mono opacity-60 tracking-tighter uppercase">
                    {t.matchDice}
                </p>
            </footer>

            {/* ── Tutorial Tooltip Overlay ─────────────────────────────────────── */}
            <div className="fixed inset-0 z-[60] pointer-events-none">
                {/* Semi-transparent backdrop (only on intro/end steps) */}
                {(step?.type === 'intro' || step?.type === 'end') && (
                    <div className="absolute inset-0 bg-black/40 pointer-events-auto" />
                )}

                {/* Tooltip */}
                <div
                    className={`absolute ${getTooltipPosition()} z-[61] pointer-events-auto max-w-[280px] lg:max-w-sm w-[85vw]`}
                >
                    <div className="pixel-card bg-[#1a101f]/95 border-2 border-theme/50 p-4 lg:p-6">
                        {/* Step counter */}
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[8px] lg:text-xs text-theme/70 font-mono uppercase">
                                {stepIdx + 1} / {SCRIPT.length}
                            </span>
                            {/* Progress dots */}
                            <div className="flex gap-1">
                                {SCRIPT.map((_, i) => (
                                    <div
                                        key={i}
                                        className={`w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full transition-all ${i <= stepIdx ? 'bg-theme' : 'bg-gray-600'
                                            }`}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Message */}
                        <p className="text-[10px] lg:text-sm text-gray-200 leading-relaxed mb-4">
                            {messageText}
                        </p>

                        {/* Buttons */}
                        <div className="flex items-center justify-between">
                            <button
                                onClick={handleSkip}
                                className="text-[8px] lg:text-xs text-gray-500 hover:text-gray-300 transition-colors uppercase tracking-wider"
                            >
                                {t.tutSkip}
                            </button>

                            {showNextBtn && (
                                <button
                                    onClick={handleNext}
                                    className="px-3 py-1.5 lg:px-5 lg:py-2.5 bg-theme text-black pixel-border text-[8px] lg:text-xs font-bold active:translate-y-0.5 transition-all uppercase"
                                >
                                    {isLastStep ? t.tutGotIt : t.tutNext}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TutorialGame;
